"use client";

import {
  createDesignProject,
  getBuilderPartnerContext,
  listDesignProjects,
  resolveBuilderPartnerContext,
  type BuilderPartnerContext,
} from "@yardzen/rest-api-client";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense, useEffect, useState } from "react";
import { LoadingSpinner } from "@yardzen/components/loading-spinner";
import { PageError } from "@yardzen/ui/components/page-error";
import {
  fetchTreatmentOnOff,
  getBuilderPartnerOnboardingPath,
  getBuilderPartnerOnboardingSplit,
  isBuilderPartnerProject,
  navigateToBuilderPartnerFlow,
} from "../../../lib/toll/builderPartnerOnboarding";

// A partner-promo arrival lands here straight from checkout, where the design
// project is created by the Stripe webhook — it may not exist on the first
// look. Bounded wait before giving up to the error state.
const PROJECT_RETRY_DELAYS_MS = [2000, 3000, 4000];

async function listProjectsWaitingForWebhook(waitForProject: boolean) {
  let { data: designProjects } = await listDesignProjects();
  if (!waitForProject) {
    return designProjects;
  }
  for (const delay of PROJECT_RETRY_DELAYS_MS) {
    if (designProjects.length > 0) break;
    await new Promise((resolve) => setTimeout(resolve, delay));
    ({ data: designProjects } = await listDesignProjects());
  }
  return designProjects;
}

function DesignProjectRedirector() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [hasError, setHasError] = useState(false);

  // Set by checkout when a partner-flagged promo was applied: call
  // the idempotent resolve endpoint — which matches the session email and
  // attaches the partner context itself — instead of only reading whatever
  // the webhook may or may not have written yet. Non-partner arrivals never
  // resolve, so they never create a context row.
  const builderPartnerPromo = searchParams.get("builderPartnerPromo") === "1";

  useEffect(() => {
    async function handleRedirect() {
      let designProjects;
      try {
        designProjects =
          await listProjectsWaitingForWebhook(builderPartnerPromo);
      } catch {
        // Without this the spinner never stops when the lookup fails.
        setHasError(true);
        return;
      }

      if (designProjects.length === 0) {
        // Legacy onboarding no longer exists; client-account sends everyone
        // who reaches it here (the old client dashboard is now only a
        // redirect), so this is the only place that can put a client right. Some of them never got a
        // design project -- a payment that confirmed while the cart pointer was
        // mid-flight is one way, and there are others -- so try to seed one
        // before giving up on them.
        //
        // The endpoint decides whether they are entitled to it: it seeds only
        // against a succeeded payment, returns the existing project rather than
        // a second one, and builds the step list from the package they actually
        // bought. A client with no purchase gets the support message below
        // instead of a free design project.
        try {
          const { data: seeded } = await createDesignProject();
          designProjects = [seeded];
        } catch {
          setHasError(true);
          return;
        }
      }

      const project = designProjects[0];
      try {
        let context: BuilderPartnerContext | null;
        if (builderPartnerPromo) {
          context = await resolveBuilderPartnerContext(
            project.accountId,
            project.id,
          );
        } else {
          context = await getBuilderPartnerContext(
            project.accountId,
            project.id,
          );
        }
        if (isBuilderPartnerProject(context)) {
          const split = getBuilderPartnerOnboardingSplit(context?.partnerKey);
          const splitOn = split ? await fetchTreatmentOnOff(split) : false;
          const partnerPath = getBuilderPartnerOnboardingPath({
            designProjectId: project.id,
            partnerKey: context?.partnerKey,
            onboardingSplitOn: splitOn,
          });
          if (partnerPath) {
            navigateToBuilderPartnerFlow(partnerPath);
            return;
          }
        }
      } catch {
        // Fail open to standard onboarding — never strand a client here.
      }
      router.replace(`/design-project/${project.id}/onboarding`);
    }
    handleRedirect();
  }, [router, builderPartnerPromo]);

  if (hasError) {
    return (
      <PageError
        title="Onboarding has moved"
        description="We couldn't find a design project for your account. Onboarding has moved to a new experience — please contact support@yardzen.com and we'll get you set up."
      />
    );
  }

  return (
    <div className="flex h-screen items-center justify-center">
      <LoadingSpinner />
    </div>
  );
}

export default function DesignProjectPage() {
  // useSearchParams requires a Suspense boundary for prerendering.
  return (
    <Suspense
      fallback={
        <div className="flex h-screen items-center justify-center">
          <LoadingSpinner />
        </div>
      }
    >
      <DesignProjectRedirector />
    </Suspense>
  );
}
